import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag, Truck, Leaf, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/lib/cart";

const NOTES = [
  { icon: Truck, text: "Free express shipping Australia-wide over $300" },
  { icon: MapPin, text: "Formulated & dispatched from Australia" },
  { icon: Leaf, text: "Cruelty free · Recyclable glass packaging" },
];

export default function ProductPage() {
  const { id } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);
  const { add, count } = useCart();

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const product = products.find((p) => p.id === id);
  const related = products.filter((p) => p.id !== id && p.category === product?.category).slice(0, 3);
  const soldOut = product && product.stock === 0;

  if (loading) return <div className="min-h-screen bg-[#FAFAFA]" data-testid="product-loading" />;

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center gap-6" data-testid="product-not-found">
        <p className="font-serif text-3xl font-light">This product is unavailable.</p>
        <Link to="/shop" className="text-xs tracking-[0.25em] uppercase text-[#9E7B3A] underline">Back to the collection</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]" data-testid="product-page">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#C5A059]/20 bg-white">
        <div className="flex items-center gap-4">
          <Link to="/shop" data-testid="back-to-shop-link" aria-label="Back to shop"
            className="text-[#4A4A4A] hover:text-[#9E7B3A] transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <img src="/assets/logo.png" alt="Australian Luxe" className="w-10 h-10 rounded-full" />
          <p className="font-serif text-lg leading-tight">Australian Luxe</p>
        </div>
        <Link to="/shop/cart" data-testid="cart-link"
          className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase hover:text-[#9E7B3A] transition-colors duration-300">
          <ShoppingBag className="w-4 h-4" /> Cart ({count})
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A]/60 mb-10" data-testid="product-breadcrumb">
          <Link to="/shop" className="hover:text-[#9E7B3A]">Collection</Link>
          <span className="mx-2 text-[#C5A059]">/</span>{product.category}
          <span className="mx-2 text-[#C5A059]">/</span><span className="text-[#9E7B3A]">{product.name}</span>
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 mb-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="relative overflow-hidden bg-white border border-[#C5A059]/15">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full aspect-[4/5] object-cover"
                data-testid="product-image" />
            ) : (
              <div className="w-full aspect-[4/5] flex flex-col items-center justify-center gap-4 bg-[#FAFAFA]" data-testid="product-image-placeholder">
                <img src="/assets/logo.png" alt="" className="w-20 h-20 rounded-full opacity-40" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A]/70">Product image coming soon</span>
              </div>
            )}
            <span className="absolute top-4 left-4 text-[9px] tracking-[0.25em] uppercase bg-white/90 text-[#9E7B3A] px-3 py-1.5">
              {soldOut ? "Sold Out" : product.badge}
            </span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="flex flex-col justify-center">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#9E7B3A] mb-4">{product.category}</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-4" data-testid="product-name">{product.name}</h1>
            <p className="font-serif text-2xl font-light text-[#9E7B3A] mb-6" data-testid="product-price">
              ${(variant ? variant.price : product.price).toFixed(2)} <span className="text-sm text-[#4A4A4A]">AUD · {product.size}</span>
            </p>
            {product.variants?.length > 0 && (
              <div className="mb-8" data-testid="variant-selector">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-3">Select Amount</p>
                <div className="flex flex-wrap gap-3">
                  {product.variants.map((v) => (
                    <button key={v.label} onClick={() => setVariant(v)} data-testid={`variant-${v.label.replace("$", "")}`}
                      className={`px-5 py-2.5 text-xs tracking-[0.15em] border transition-colors duration-300 ${
                        variant?.label === v.label ? "border-[#9E7B3A] text-[#9E7B3A] bg-[#C5A059]/10" : "border-[#C5A059]/40 text-[#4A4A4A] hover:border-[#9E7B3A]"
                      }`}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-base font-light text-[#4A4A4A] leading-relaxed mb-10" data-testid="product-description">
              {product.description}
            </p>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-[#C5A059]/40">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus" aria-label="Decrease"
                  className="w-11 h-11 flex items-center justify-center hover:text-[#9E7B3A] transition-colors duration-300">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-10 text-center text-sm" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(product.stock, q + 1))} data-testid="qty-plus" aria-label="Increase"
                  className="w-11 h-11 flex items-center justify-center hover:text-[#9E7B3A] transition-colors duration-300">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {!soldOut && product.stock <= 5 && (
                <span className="text-xs text-[#9E7B3A]" data-testid="low-stock-note">Only {product.stock} left</span>
              )}
            </div>

            <button
              disabled={soldOut || (product.variants?.length > 0 && !variant)}
              onClick={() => { for (let n = 0; n < qty; n++) add(product, variant); toast.success(variant ? `${product.name} ${variant.label} added to cart` : `${product.name} added to cart`); }}
              data-testid="add-to-cart-button"
              className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-4 hover:bg-[#9E7B3A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300 mb-10">
              {soldOut ? "Sold Out" : product.variants?.length > 0 && !variant ? "Select an Amount" : "Add to Cart"}
            </button>

            <div className="space-y-3 border-t border-[#C5A059]/20 pt-8">
              {NOTES.map(({ icon: Icon, text }) => (
                <p key={text} className="flex items-center gap-3 text-xs text-[#4A4A4A]">
                  <Icon className="w-4 h-4 text-[#C5A059]" /> {text}
                </p>
              ))}
            </div>
          </motion.div>
        </div>

        {related.length > 0 && (
          <div data-testid="related-products">
            <h2 className="font-serif text-3xl font-light mb-10">You may also <span className="italic text-[#9E7B3A]">love</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link key={p.id} to={`/shop/product/${p.id}`} className="group" data-testid={`related-${p.id}`}>
                  <div className="overflow-hidden bg-white border border-[#C5A059]/15 mb-4">
                    <img src={p.image_url || "/assets/logo.png"} alt={p.name}
                      className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <h3 className="font-serif text-lg font-light group-hover:text-[#9E7B3A] transition-colors duration-300">{p.name}</h3>
                  <p className="text-sm text-[#4A4A4A]">${p.price.toFixed(2)} AUD</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
